import { Component, OnInit, OnDestroy } from '@angular/core';
import { onSnapshot, getDocs } from '@angular/fire/firestore';
import { ReliefService } from 'src/app/services/relief.service';
import * as CryptoJS from 'crypto-js'; 
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-reliefs',
  templateUrl: './reliefs.component.html',
  styleUrls: ['./reliefs.component.css']
})
export class ReliefsComponent implements OnInit, OnDestroy {
  // Variables de environment
  isBeta = environment.beta;
  isProduction = environment.production;
  version = environment.version;
  
  reliefs: any = [];
  decrypted: string = '';
  isModalVisible: boolean = false;
  modalData: string = '';
  modalFeeling: string = '';
  deleteId: string | null = null;
  showDeleteModal: boolean = false;
  isLoading: boolean = false;
  showSuccess: boolean = false;
  
  private autoDeleteInterval: any;

  constructor(private _reliefService: ReliefService) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  async initializeComponent() {
    try {
      // 1. Verificar eliminaciones automáticas al cargar
      await this.checkAutoDeleteOnLoad();
      
      // 2. Cargar datos actualizados
      this.getReliefs();
      
      // 3. Configurar verificación periódica cada 2 minutos
      this.autoDeleteInterval = setInterval(() => {
        this.checkAutoDelete();
      }, 120000); // 2 minutos
      
      // 4. Listener para cerrar modal con Esc
      window.addEventListener('keydown', this.handleEscKey.bind(this));
      
    } catch (error) {
      console.error('Error initializing component:', error);
      // Cargar datos aunque haya error en la verificación
      this.getReliefs();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.handleEscKey.bind(this));
    // Limpiar el intervalo al destruir el componente
    if (this.autoDeleteInterval) {
      clearInterval(this.autoDeleteInterval);
    }
  }

  handleEscKey(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isModalVisible) {
      this.closeModal();
      
    }else if (event.key === 'Escape' && this.showDeleteModal) {
      this.cancelDelete();
    }
  }

  getReliefs() {
    this.reliefs = [];
    const q = this._reliefService.getReliefs();
    onSnapshot(q, snapshot => {
      this.reliefs = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        const id = doc.id;
        this.reliefs.push({ id, ...(data as Object) });
      });
    });
  }

  decrypt(data: string, key: string, feeling: string) {
    var bytes = CryptoJS.AES.decrypt(data, key);
    var decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    this.decrypted = decryptedData;
    this.openModal(this.decrypted, feeling);
  }

  openModal(data: any, feeling: string) {
    this.modalData = data;
    this.modalFeeling = feeling;
    this.isModalVisible = true;
  }

  closeModal() {
    this.isModalVisible = false;
  }

  deleteThought(id: any) {
    this.deleteId = id;
    this.showDeleteModal = true;
  }
  confirmDelete() {
    if (this.deleteId) {
      this.isLoading = true;
      this.showDeleteModal = false;
      this._reliefService.deleteRelief(this.deleteId).then(() => {
        // NO limpiar manualmente aquí: this.reliefs = [];
        // Opcional: muestra un mensaje de éxito con tu modal de info o un toast propio
        setTimeout(() => {
          this.isLoading = false;
          this.showSuccess = true;
          setTimeout(() => {
            this.showSuccess = false;
          }, 1500); // Mensaje de éxito por 1.5 segundos
        }, 500); // Spinner por 1.2 segundos
        
        // El snapshot listener se actualizará automáticamente
        // y getReliefs() se ejecutará con la nueva data
      }).catch(() => {
        this.isLoading = false;
        // Opcional: muestra un mensaje de error
      });
    }
    this.showDeleteModal = false;
    this.deleteId = null;
  }

  cancelDelete() {
    this.showDeleteModal = false;
    this.deleteId = null;

  }

  checkAutoDelete() {
    const now = new Date();
    let foundExpired = false;
    
    // Solo verificar si hay elementos en la lista
    if (this.reliefs.length === 0) return;
    
    this.reliefs.forEach((relief: any) => {
      if (relief.deleteIn24h && relief.deleteAt) {
        // Verificar si es Timestamp o Date
        let deleteTime: Date;
        if (typeof relief.deleteAt.toDate === 'function') {
          deleteTime = relief.deleteAt.toDate(); // Es un Timestamp
        } else {
          deleteTime = relief.deleteAt; // Ya es un Date
        }
        
        if (now >= deleteTime) {
          console.log(`Auto-deleting relief ${relief.id} at ${now}`);
          this.autoDeleteRelief(relief.id);
          foundExpired = true;
        }
      }
    });
    
    if (!foundExpired) {
      console.log('No expired reliefs found during periodic check');
    }
  }

  autoDeleteRelief(id: string) {
    this._reliefService.deleteRelief(id).then(() => {
      console.log(`Relief ${id} deleted automatically`);
      // No actualizar manualmente la lista
      // El snapshot listener se encargará de actualizar automáticamente
    }).catch((error) => {
      console.error('Error auto-deleting relief:', error);
    });
  }

  async checkAutoDeleteOnLoad() {
    console.log('Checking for expired reliefs on page load...');

    return new Promise((resolve) => {
      const q = this._reliefService.getReliefs();
      getDocs(q).then((snapshot) => {
        const now = new Date();
        const deletePromises: Promise<void>[] = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data['deleteIn24h'] && data['deleteAt']) {
            const deleteTime = this.getDateFromFirebaseTimestamp(data['deleteAt']);

            if (now >= deleteTime) {
              console.log(`Auto-deleting expired relief ${doc.id} at ${now}`);
              deletePromises.push(this._reliefService.deleteRelief(doc.id));
            }
          }
        });

        Promise.all(deletePromises).then(() => {
          console.log(`Deleted ${deletePromises.length} expired reliefs`);
          resolve(void 0);
        }).catch((error) => {
          console.error('Error deleting expired reliefs:', error);
          resolve(void 0);
        });
      });
    });
  }

  private getDateFromFirebaseTimestamp(timestamp: any): Date {
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate(); // Es un Timestamp de Firebase
    } else if (timestamp instanceof Date) {
      return timestamp; // Ya es un Date
    } else {
      return new Date(timestamp); // Convertir string o number a Date
    }
  }

  getClockHandPosition(deleteAt: any): string {
    if (!deleteAt) return "12,12 12,7"; // Posición por defecto (12 en punto)
    
    const deleteTime = deleteAt.toDate();
    const hour = deleteTime.getHours() % 12; // Convertir a formato 12 horas
    const minute = deleteTime.getMinutes();
    
    // Calcular el ángulo de la manecilla
    const hourAngle = (hour * 30) + (minute * 0.5); // 30° por hora + 0.5° por minuto
    const radians = (hourAngle - 90) * (Math.PI / 180); // -90 para empezar desde arriba
    
    const centerX = 12;
    const centerY = 12;
    const handLength = 5;
    
    const endX = centerX + handLength * Math.cos(radians);
    const endY = centerY + handLength * Math.sin(radians);
    
    return `${centerX},${centerY} ${endX.toFixed(1)},${endY.toFixed(1)}`;
  }

  getHourHandPosition(deleteAt: any): string {
    if (!deleteAt) return "12,12 12,8"; // Posición por defecto (12 en punto)
    
    const deleteTime = deleteAt.toDate();
    const hour = deleteTime.getHours() % 12; // Convertir a formato 12 horas
    const minute = deleteTime.getMinutes();
    
    // Calcular el ángulo de la manecilla de horas
    const hourAngle = (hour * 30) + (minute * 0.5); // 30° por hora + 0.5° por minuto
    const radians = (hourAngle - 90) * (Math.PI / 180); // -90 para empezar desde arriba
    
    const centerX = 12;
    const centerY = 12;
    const handLength = 4; // Más corta que la de minutos
    
    const endX = centerX + handLength * Math.cos(radians);
    const endY = centerY + handLength * Math.sin(radians);
    
    return `${centerX},${centerY} ${endX.toFixed(1)},${endY.toFixed(1)}`;
  }

  getMinuteHandPosition(deleteAt: any): string {
    if (!deleteAt) return "12,12 12,6"; // Posición por defecto (12 en punto)
    
    const deleteTime = deleteAt.toDate();
    const minute = deleteTime.getMinutes();
    
    // Calcular el ángulo de la manecilla de minutos
    const minuteAngle = minute * 6; // 6° por minuto (360° / 60 minutos)
    const radians = (minuteAngle - 90) * (Math.PI / 180); // -90 para empezar desde arriba
    
    const centerX = 12;
    const centerY = 12;
    const handLength = 6; // Más larga que la de horas
    
    const endX = centerX + handLength * Math.cos(radians);
    const endY = centerY + handLength * Math.sin(radians);
    
    return `${centerX},${centerY} ${endX.toFixed(1)},${endY.toFixed(1)}`;
  }
}
